<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import { BookOpen, ExternalLink, Loader2, Search } from "@lucide/vue";

import { useLocalization } from "@/providers/Localization";
import { Material } from "@/types/Views/MaterialDatabase/Material";

// Makes it explicit which EPD a material is mapped to (RTLCA-5028): the mapped
// EPD name + provenance, plus quick links to inspect it, open it in the Library,
// or open the original source declaration.
const props = defineProps<{
  mapping: Material | null;
  // Inspecting re-reads the EPD from the EPD service (RTLCA-5544), so the button has a pending state.
  isInspecting?: boolean;
}>();

const emit = defineEmits<{
  inspect: [];
}>();

const router = useRouter();
const localization = useLocalization();

const epdName = computed(() =>
  props.mapping ? localization.getEpdTranslation(props.mapping.names, props.mapping.name || "Unknown") : ""
);

const datasource = computed(() => {
  if (!props.mapping) return "";
  return props.mapping.datasourceName || props.mapping.datasourceNames?.join(", ") || "";
});

const sourceLink = computed(() => props.mapping?.epdInfo?.link ?? "");
const declarationNumber = computed(() => props.mapping?.epdInfo?.declarationNumber ?? "");

// Numeric keys mirror EpdProductIndustryType in the .NET API
// (src/Shared/Gaiup.Rtlca.Core/Core/BuildingProjects/Constants/EpdProductIndustryTpe.cs —
// INDUSTRY = 0, PRODUCT = 1, GENERIC = 2). Kept local rather than read from useEnum().productTypes:
// that store's display names are "Industry" / "Product" / "Generic", and this box deliberately
// words them differently ("Generic data", not "Generic EPD").
const epdProductIndustryTypeLabels: Record<number, string> = {
  0: "Industry EPD",
  1: "Product EPD",
  2: "Generic data",
};

// What kind of thing is mapped, so a construction is not visually identical to an industry EPD
// (RTLCA-5426). A construction wins over custom when a mapping is both.
const mappedTypeLabel = computed(() => {
  const mapping = props.mapping;
  if (!mapping) return "";
  // `isConstruction` is `bool?` on the backend while the frontend type declares it non-optional,
  // so older rows can arrive as null — test for `true` explicitly and let null fall through to
  // the material branches below rather than being read as either answer.
  if (mapping.isConstruction === true) return "Construction";
  if (mapping.isCustom || !mapping.epdInfo) return "Custom data";
  return epdProductIndustryTypeLabels[mapping.epdInfo.epdProductIndustryType] ?? "";
});

// New-design ghost action button (Lucide + token styling), matches the mapping surface.
const actionButtonClass =
  "focus-ring interactive-active t-small text-type-default duration-fast ease-standard inline-flex items-center gap-1.5 rounded-8 border border-slate-200 bg-slate-0 px-3 py-1.5 transition-colors hover:bg-slate-100";

const openInLibrary = () => {
  if (!props.mapping?.id) return;
  const route = router.resolve({ name: "libraryDetail", params: { id: props.mapping.id } });
  window.open(route.href, "_blank");
};

const openSource = () => {
  if (sourceLink.value) window.open(sourceLink.value, "_blank", "noopener");
};
</script>

<template>
  <div class="rounded-8 bg-slate-0 border border-slate-200 p-4">
    <!-- Permanent two-line title: it names the box, never what happens to be mapped into it. -->
    <div class="flex flex-col">
      <span class="t-caption text-type-muted tracking-wide uppercase">Mapped</span>
      <span class="t-footnote text-type-muted tracking-wide uppercase">Environmental data</span>
    </div>

    <template v-if="mapping">
      <!-- The name is what the eye should land on, so it carries the weight and size here; the type
           is a quiet category tag above it rather than a competing bold line (RTLCA-5426 review). -->
      <div class="mt-3 flex flex-col gap-0.5">
        <span
          v-if="mappedTypeLabel"
          class="t-footnote-bold text-type-muted tracking-wide uppercase">
          {{ mappedTypeLabel }}
        </span>
        <span class="t-sh4 text-type-default">{{ epdName }}</span>
      </div>

      <dl class="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <template v-if="datasource">
          <dt class="t-xs text-type-muted">Data source</dt>
          <dd class="t-xs text-type-default">{{ datasource }}</dd>
        </template>
        <dt class="t-xs text-type-muted">Declaration owner</dt>
        <dd class="t-xs text-type-default">{{ mapping.declarationOwnerName || "—" }}</dd>
        <template v-if="declarationNumber">
          <dt class="t-xs text-type-muted">Declaration no.</dt>
          <dd class="t-xs text-type-default">{{ declarationNumber }}</dd>
        </template>
      </dl>

      <div class="mt-3 flex flex-col flex-wrap gap-2">
        <!-- Gated on the id for the same reason "Open in Library" is: without one there is no EPD
             record to re-read, and inspecting means re-reading it. -->
        <button
          v-if="mapping.id"
          type="button"
          :disabled="isInspecting"
          :class="[actionButtonClass, { 'interactive-disabled': isInspecting }]"
          @click="emit('inspect')">
          <Loader2
            v-if="isInspecting"
            class="icon-sm animate-spin"
            :stroke-width="1.5" />
          <Search
            v-else
            class="icon-sm"
            :stroke-width="1.5" />
          Inspect
        </button>
        <button
          v-if="mapping.id"
          type="button"
          :class="actionButtonClass"
          @click="openInLibrary">
          <BookOpen
            class="icon-sm"
            :stroke-width="1.5" />
          Open in Library
        </button>
        <button
          v-if="sourceLink"
          type="button"
          :class="actionButtonClass"
          @click="openSource">
          <ExternalLink
            class="icon-sm"
            :stroke-width="1.5" />
          View source
        </button>
      </div>
    </template>

    <span
      v-else
      class="t-small text-type-muted mt-2 block">
      No EPD mapped yet.
    </span>
  </div>
</template>
